import { inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";

export interface Breadcrumb {
  route: string;
  ts: number;
}

const MAX_BREADCRUMBS = 10;

/**
 * Collects the last navigation route paths (path only — query strings and
 * fragments are stripped so no PII in URL params can leak) and the session
 * start timestamp. Shared by error reporting (Ticket 2) and navigation
 * timing (Ticket 4).
 *
 * Instantiated at bootstrap via the app initializer in app.config.ts so the
 * session start and first navigations are actually captured — do not rely on
 * lazy injection alone.
 */
@Injectable({ providedIn: "root" })
export class NavigationBreadcrumbsService {
  private readonly sessionStartTs = Date.now();
  private readonly crumbs: Breadcrumb[] = [];

  constructor() {
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.record(stripQueryAndFragment(event.urlAfterRedirects));
      });
  }

  get breadcrumbs(): Breadcrumb[] {
    return [...this.crumbs];
  }

  get sessionAgeMs(): number {
    return Date.now() - this.sessionStartTs;
  }

  private record(route: string): void {
    this.crumbs.push({ route, ts: Date.now() });
    if (this.crumbs.length > MAX_BREADCRUMBS) {
      this.crumbs.shift();
    }
  }
}

export function stripQueryAndFragment(url: string): string {
  return url.split(/[?#]/, 1)[0];
}
